// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useMemo, useRef, useState, type FormEvent } from "react";
import {
  budgetForPeriod,
  periodRange,
  rebalance,
  toCents,
  toDollars,
  toPercent,
  type BudgetLine,
  type Cents,
  type RebalanceMode,
} from "@almanac/domain";
import { SplitSlider } from "../components/SplitSlider.js";
import { useApp } from "../lib/app.js";
import { setAllUnits, setLines, setPool, setSplit, splitByActuals, splitFor } from "../lib/budgetTree.js";
import { categoryTree, isCustom, MAX_DEPTH, type Category } from "../lib/categories.js";
import { commitments, scheduledByCategory, stillExpected } from "../lib/commitments.js";
import { actualsThisMonth, recentMonthlyActuals, rolledUp } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import { activeSaves, PURCHASES_LINE, savingsPlans } from "../lib/purchases.js";
import { useStore } from "../lib/store.js";
import { PACE_MARK } from "./Purchases.js";

type Unit = NonNullable<BudgetLine["unit"]>;

/** How a line is shown, and what its lock holds. */
const unitOf = (line: BudgetLine): Unit => line.unit ?? "dollars";

/** Locking holds the unit shown at the time. */
const toggleLock = (line: BudgetLine): BudgetLine => ({ ...line, locked: !line.locked, unit: unitOf(line) });

/** The id of the panel a category's chevron opens. */
const panelFor = (id: string) => `subs-${id.replace(/\//g, "-")}`;

export function Budgets() {
  const { store, today, announce } = useApp();
  const { plan, transactions, profile, custom, events, purchases } = useStore(store);
  const { t, money, percent, category } = useI18n();
  const helpId = useId();
  const actuals = actualsThisMonth(transactions, today);
  const month = periodRange("month", today);
  const scheduled = scheduledByCategory(stillExpected(commitments(events, transactions, month.start, month.end), today));
  const inUse = useMemo(() => new Set(transactions.flatMap((tx) => (tx.category ? [tx.category] : []))), [transactions]);
  const tree = useMemo(() => categoryTree(profile, custom, inUse), [profile, custom, inUse]);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggleOpen = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };
  const maxLine = Math.max(...plan.lines.map((l) => Math.max(l.amount, rolledUp(actuals, l.id))));
  // Hold the scale steady while a slider is moving so the track does not jump.
  const scaleRef = useRef<number | null>(null);
  const scale = scaleRef.current ?? Math.max(plan.pool * 0.4, maxLine * 1.15);
  const total = plan.lines.reduce((s, l) => s + l.amount, 0);
  const announceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const change = (id: string, amount: number) => {
    scaleRef.current = scale;
    const result = rebalance(plan.lines, id, amount, plan.mode);
    store.setPlan(setLines(plan, result.lines));
    if (announceTimer.current) clearTimeout(announceTimer.current);
    announceTimer.current = setTimeout(() => {
      scaleRef.current = null;
      announce(t("budgets.rebalanced", { name: category(id), amount: money(result.applied) }));
    }, 700);
  };

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="toolbar">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="pool">{t("budgets.pool")}</label>
            <input
              id="pool"
              type="number"
              min={0}
              step={50}
              inputMode="decimal"
              value={toDollars(plan.pool)}
              aria-describedby="pool-help"
              onChange={(e) => store.setPlan(setPool(plan, toCents(Number(e.target.value) || 0)))}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <span className="label" id="all-units">
              {t("budgets.units")}
            </span>
            <div className="row" role="group" aria-labelledby="all-units">
              <button type="button" onClick={() => store.setPlan(setAllUnits(plan, "dollars"))}>
                {t("budgets.allDollars")}
              </button>
              <button type="button" onClick={() => store.setPlan(setAllUnits(plan, "percent"))}>
                {t("budgets.allPercent")}
              </button>
            </div>
          </div>
          <fieldset>
            <legend>{t("budgets.mode")}</legend>
            <div className="choices">
              {(["equal", "proportional"] as RebalanceMode[]).map((mode) => (
                <label key={mode}>
                  <input type="radio" name="mode" checked={plan.mode === mode} onChange={() => store.setPlan({ ...plan, mode })} />
                  {t(mode === "equal" ? "budgets.modeEqual" : "budgets.modeProportional")}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <p className="hint" id="pool-help">
          {t("budgets.poolHelp")}
        </p>
      </div>

      <section className="card" aria-labelledby="sliders-title" aria-describedby={helpId}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
          <h2 id="sliders-title" style={{ margin: 0 }}>
            {t("period.month")}
          </h2>
          <span className="key" aria-hidden="true">
            <span>
              <i style={{ background: "var(--budget)" }} />
              {t("budgets.budget")}
            </span>
            <span>
              <i style={{ background: "var(--actual)" }} />
              {t("budgets.actual")}
            </span>
          </span>
        </div>
        <p className="hint" id={helpId} style={{ marginBottom: 14 }}>
          {t("budgets.help")}
        </p>
        <div className="sliders">
          {plan.lines.map((line) => {
            const node = tree.find((c) => c.id === line.id);
            const setLine = (next: BudgetLine) => store.setPlan({ ...plan, lines: plan.lines.map((l) => (l.id === line.id ? next : l)) });
            const isPurchases = line.id === PURCHASES_LINE;
            const count = isPurchases ? activeSaves(purchases).length : (node?.children.length ?? 0);
            const expandable = isPurchases || (node && node.id !== "savings");
            const name = category(line.id);
            return (
              <div key={line.id} className={isPurchases ? "budget-line purchases-line" : "budget-line"}>
                <SplitSlider
                  id={line.id}
                  name={name}
                  budget={line.amount}
                  actual={rolledUp(actuals, line.id)}
                  scheduled={rolledUp(scheduled, line.id)}
                  pool={plan.pool}
                  scale={scale}
                  locked={line.locked}
                  fixed={isPurchases}
                  units={unitOf(line)}
                  onUnits={(unit) => setLine({ ...line, unit })}
                  onChange={(amount) => change(line.id, amount)}
                  onToggleLock={() => setLine(toggleLock(line))}
                  expand={
                    expandable
                      ? {
                          open: open.has(line.id),
                          count,
                          controls: panelFor(line.id),
                          label: t(isPurchases ? "budgets.goalsToggle" : "budgets.subsToggle", { name, count }),
                          onToggle: () => toggleOpen(line.id),
                        }
                      : undefined
                  }
                />
                {isPurchases && (
                  <p className="hint" style={{ margin: 0 }}>
                    {t("budgets.purchasesLine")}
                  </p>
                )}
                {isPurchases && open.has(line.id) && <PurchaseGoals line={line} />}
                {!isPurchases && node && expandable && open.has(line.id) && (
                  <SubBudgets node={node} total={line.amount} actuals={actuals} scheduled={scheduled} open={open} onToggle={toggleOpen} />
                )}
              </div>
            );
          })}
        </div>
        <p style={{ marginTop: 16 }}>
          <strong>{t("budgets.total")}:</strong> {money(total)} ({percent(toPercent(total, plan.pool), 0)}) ·{" "}
          {t("period.week")}: {money(budgetForPeriod(total, periodRange("week", today)))} · {t("period.year")}:{" "}
          {money(budgetForPeriod(total, periodRange("year", today)))}
        </p>
      </section>
    </>
  );
}

/** The disclosure under a category: its split among sub-categories, and a way to add one. */
function SubBudgets({
  node,
  total,
  actuals,
  scheduled,
  open,
  onToggle,
}: {
  node: Category;
  total: Cents;
  actuals: ReadonlyMap<string, Cents>;
  scheduled: ReadonlyMap<string, Cents>;
  open: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  const { store, announce, today } = useApp();
  const { plan, transactions } = useStore(store);
  const { t, money, category } = useI18n();
  const recent = useMemo(() => recentMonthlyActuals(transactions, today), [transactions, today]);
  const name = category(node.id);
  const lines = splitFor(
    plan,
    node.id,
    node.children.map((c) => c.id),
  );
  const actualOf = (id: string) => (id === node.id ? (actuals.get(id) ?? 0) : rolledUp(actuals, id));
  const scheduledOf = (id: string) => (id === node.id ? (scheduled.get(id) ?? 0) : rolledUp(scheduled, id));
  const scale = Math.max(1, total, ...lines.map((l) => actualOf(l.id))) * 1.15;

  const setLine = (next: BudgetLine) => store.setPlan(setSplit(plan, node.id, lines.map((l) => (l.id === next.id ? next : l))));

  return (
    <div className="sub-budgets">
        <div id={panelFor(node.id)} role="group" aria-label={t("budgets.subs", { name })} className="sub-panel">
          <p className="hint">{t("budgets.subsHelp", { name, amount: money(total) })}</p>
          {(() => {
            const suggested = splitByActuals(lines, (id) => (id === node.id ? (recent.get(id) ?? 0) : rolledUp(recent, id)));
            return (
              suggested && (
                <button
                  type="button"
                  style={{ justifySelf: "start" }}
                  onClick={() => {
                    store.setPlan(setSplit(plan, node.id, suggested));
                    announce(t("budgets.fromRecentDone", { name }));
                  }}
                >
                  {t("budgets.fromRecent")}
                </button>
              )
            );
          })()}
          {lines.map((line) => {
            const child = node.children.find((c) => c.id === line.id);
            const label = line.id === node.id ? t("budgets.rest", { name }) : category(line.id);
            return (
              <div key={line.id} className="budget-line">
                <SplitSlider
                  id={line.id.replace(/\//g, "-")}
                  name={label}
                  budget={line.amount}
                  actual={actualOf(line.id)}
                  scheduled={scheduledOf(line.id)}
                  pool={total}
                  scale={scale}
                  locked={line.locked}
                  units={unitOf(line)}
                  onUnits={(unit) => setLine({ ...line, unit })}
                  onChange={(amount) => {
                    const result = rebalance(lines, line.id, amount, plan.mode);
                    store.setPlan(setSplit(plan, node.id, result.lines));
                  }}
                  onToggleLock={() => setLine(toggleLock(line))}
                  expand={
                    child && child.children.length > 0
                      ? {
                          open: open.has(child.id),
                          count: child.children.length,
                          controls: panelFor(child.id),
                          label: t("budgets.subsToggle", { name: label, count: child.children.length }),
                          onToggle: () => onToggle(child.id),
                        }
                      : undefined
                  }
                />
                {child && isCustom(child.id) && (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      if (!confirm(t("budgets.confirmRemove", { name: label, parent: name }))) return;
                      store.removeCategory(child.id);
                      announce(t("budgets.removed", { name: label, parent: name }));
                    }}
                  >
                    {t("budgets.removeSub", { name: label })}
                  </button>
                )}
                {child && child.children.length > 0 && open.has(child.id) && (
                  <SubBudgets node={child} total={line.amount} actuals={actuals} scheduled={scheduled} open={open} onToggle={onToggle} />
                )}
              </div>
            );
          })}
          {node.depth < MAX_DEPTH && <AddCategory places={[node, ...node.children.filter((c) => c.depth < MAX_DEPTH && !isCustom(c.id))]} />}
        </div>
    </div>
  );
}

/** The goals the large-purchases line is saving for, with what each gets this month in the line's unit. */
function PurchaseGoals({ line }: { line: BudgetLine }) {
  const { store, today } = useApp();
  const { plan, purchases } = useStore(store);
  const { t, money, percent, date } = useI18n();
  const goals = activeSaves(purchases);
  const plans = savingsPlans(purchases, line.amount, plan.purchaseLimit ?? null, today);
  const name = t("cat.purchases");
  const share = (cents: Cents) => (unitOf(line) === "percent" ? percent(toPercent(cents, line.amount), 1) : money(cents));
  return (
    <div className="sub-budgets">
      <div id={panelFor(line.id)} role="group" aria-label={t("budgets.goals", { name })} className="sub-panel">
        {goals.length === 0 ? (
          <p className="hint">{t("budgets.noGoals")}</p>
        ) : (
          <ul className="goal-list">
            {goals.map((p) => {
              const g = plans.get(p.id)!;
              return (
                <li key={p.id}>
                  <div className="row goal-head">
                    <strong>{p.name}</strong>
                    <span className="goal-share">{t("budgets.goalShare", { amount: share(g.funded) })}</span>
                    <span className={`pace pace-${g.pace}`}>
                      <span aria-hidden="true">{PACE_MARK[g.pace]}</span> {t(`purchases.pace.${g.pace}`)}
                    </span>
                  </div>
                  <progress className="goal-progress" max={100} value={g.percentComplete} aria-label={t("purchases.progressLabel", { name: p.name })} />
                  <p className="hint" style={{ margin: 0 }}>
                    {t("purchases.progress", { saved: money(p.saved), price: money(p.price), percent: percent(g.percentComplete, 0) })} ·{" "}
                    {t("budgets.goalBy", { date: date(p.targetDate, "short") })}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
        <a href="#/purchases">{t("budgets.purchasesOpen")}</a>
      </div>
    </div>
  );
}

/** Adds a sub-category under the open category, or one level further down under one of its sub-categories. */
function AddCategory({ places }: { places: Category[] }) {
  const { store, announce } = useApp();
  const { t, category } = useI18n();
  const [name, setName] = useState("");
  const [under, setUnder] = useState(places[0]!.id);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const parent = places.find((p) => p.id === under) ?? places[0]!;
  const parentName = category(parent.id);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (parent.children.some((c) => category(c.id).toLowerCase() === trimmed.toLowerCase())) {
      setError(t("budgets.nameTaken"));
      return;
    }
    store.addCategory(parent.id, trimmed);
    announce(t("budgets.added", { name: trimmed, parent: parentName }));
    setName("");
    setError(null);
  };

  return (
    <form className="row add-category" onSubmit={submit}>
      <label htmlFor={inputId} className="visually-hidden">
        {t("budgets.addSub", { name: parentName })}
      </label>
      {places.length > 1 && (
        <select aria-label={t("budgets.addUnder")} value={under} onChange={(e) => setUnder(e.target.value)}>
          {places.map((p) => (
            <option key={p.id} value={p.id}>
              {t("budgets.under", { name: category(p.id) })}
            </option>
          ))}
        </select>
      )}
      <input
        id={inputId}
        type="text"
        maxLength={60}
        placeholder={t("budgets.addSub", { name: parentName })}
        value={name}
        aria-invalid={error ? true : undefined}
        onChange={(e) => setName(e.target.value)}
      />
      <button type="submit" disabled={!name.trim()}>
        {t("budgets.addSubButton")}
      </button>
      {error && (
        <span className="form-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}
